import { formatMoney, formatPct, toNumber } from "../../lib/format-fr.js";

export type CascadeBlock = {
  /** Etiqueta principal (ex: "Marge globale") */
  label: string;
  /** Fórmula compacta exibida abaixo do label (ex: "CA − Achats consommés") */
  formula?: string;
  /** Valor cumulativo (realizado no período já encerrado) */
  cumulative: number | string | null | undefined;
  /** Valor anualizado (projeção ano inteiro) — opcional */
  annualized?: number | string | null | undefined;
  /** Razão sobre o CA HT para indicar margem percentual */
  pctOfCaHt?: number | null;
  /** Tom de destaque do bloco */
  tone?: "default" | "emphasis" | "muted";
};

/**
 * Cascata visual do SIG (PRD §3-§4): blocos encadeados verticalmente,
 * com seta indicando "deduz-se" entre cada par. Cada bloco mostra o
 * cumulativo, o anualizado e % do CA HT.
 */
export function CascadeSIG({ blocks }: { blocks: ReadonlyArray<CascadeBlock> }) {
  return (
    <ol className="flex flex-col items-stretch gap-0">
      {blocks.map((b, i) => {
        const cum = toNumber(b.cumulative);
        const negative = cum !== null && cum < 0;
        const toneCls =
          b.tone === "emphasis"
            ? "border-lime/40 bg-lime/5"
            : b.tone === "muted"
              ? "border-neutral-800 bg-neutral-900/40"
              : "border-neutral-800 bg-neutral-900";
        return (
          <li key={i} className="flex flex-col items-center">
            <div
              className={`w-full max-w-2xl rounded-lg border ${toneCls} px-5 py-3 flex items-center justify-between gap-4`}
            >
              <div className="flex-1">
                <div className="text-sm font-semibold text-neutral-100">{b.label}</div>
                {b.formula && (
                  <div className="text-[11px] text-neutral-500 mt-0.5 font-mono">{b.formula}</div>
                )}
              </div>
              <div className="text-right">
                <div
                  className={`text-lg font-mono tabular-nums font-semibold ${
                    negative ? "text-red-300" : "text-neutral-100"
                  }`}
                >
                  {formatMoney(b.cumulative)}
                </div>
                {b.annualized !== undefined && (
                  <div className="text-xs text-neutral-500 font-mono tabular-nums">
                    Annualisé: {formatMoney(b.annualized)}
                  </div>
                )}
                {b.pctOfCaHt !== undefined && b.pctOfCaHt !== null && (
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    {formatPct(b.pctOfCaHt, { alreadyPct: true })} du CA
                  </div>
                )}
              </div>
            </div>
            {i < blocks.length - 1 && (
              <div className="flex flex-col items-center" aria-hidden>
                <div className="w-px h-3 bg-neutral-700" />
                <div className="text-neutral-600 text-xs leading-none">▼</div>
                <div className="w-px h-3 bg-neutral-700" />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
